/**
 * Approval Workflow Repository
 *
 * FERP Principles:
 * - Domain-agnostic: Works across healthcare, finance, legal workflows
 * - Data-driven: All approval data from backend (DynamoDB)
 * - Repository pattern: All data access through this repository
 * - Life-critical: Approval workflows require complete audit trails
 * - Single code path: Same repository for all approval workflows
 */

import { gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Types (from backend GraphQL schema)
// ============================================================================

export enum ApprovalStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    UNDER_REVIEW = 'UNDER_REVIEW',
    REVISION_REQUESTED = 'REVISION_REQUESTED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    EXPIRED = 'EXPIRED',
}

export enum DecisionType {
    APPROVED = 'APPROVED',
    APPROVED_WITH_SUGGESTIONS = 'APPROVED_WITH_SUGGESTIONS',
    REVISION_REQUESTED = 'REVISION_REQUESTED',
    REJECTED = 'REJECTED',
}

export interface ApproverSpec {
    role: string;
    certifications?: string[];
    minimumExperience?: number;
}

export interface RevisionItem {
    fieldId: string;
    issue: string;
    suggestedFix?: string;
}

export interface ApprovalDecision {
    decisionId: string;
    approverId: string;
    approverName: string;
    approverRole: string;
    decisionType: DecisionType;
    comments?: string;
    revisionItems: RevisionItem[];
    decidedAt: string;
}

export interface ApprovalWorkflowInstance {
    instanceId: string;
    templateId: string;
    templateVersion: string;
    tenant: string;
    domain: string;
    criticality: string;
    requiredApprovers: ApproverSpec[];
    status: ApprovalStatus;
    decisions: ApprovalDecision[];
    deadline: string;
    dualControlRequired: boolean;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
}

export interface RecordDecisionInput {
    instanceId: string;
    approverId: string;
    approverName: string;
    approverRole: string;
    decisionType: DecisionType;
    comments?: string;
    revisionItems?: RevisionItem[];
}

// ============================================================================
// GraphQL Queries & Mutations
// ============================================================================

const GET_APPROVAL_INSTANCE = gql`
    query GetApprovalWorkflowInstance($instanceId: String!) {
        getApprovalWorkflowInstance(instanceId: $instanceId) {
            instanceId
            templateId
            templateVersion
            tenant
            domain
            criticality
            requiredApprovers {
                role
                certifications
                minimumExperience
            }
            status
            decisions {
                decisionId
                approverId
                approverName
                approverRole
                decisionType
                comments
                revisionItems {
                    fieldId
                    issue
                    suggestedFix
                }
                decidedAt
            }
            deadline
            dualControlRequired
            createdAt
            createdBy
            updatedAt
            updatedBy
        }
    }
`;

const LIST_PENDING_APPROVALS = gql`
    query ListPendingApprovals($tenant: String!, $approverId: String!) {
        listPendingApprovals(tenant: $tenant, approverId: $approverId) {
            instanceId
            templateId
            templateVersion
            tenant
            domain
            criticality
            requiredApprovers {
                role
                certifications
                minimumExperience
            }
            status
            decisions {
                decisionId
                approverId
                approverName
                approverRole
                decisionType
                comments
                revisionItems {
                    fieldId
                    issue
                    suggestedFix
                }
                decidedAt
            }
            deadline
            dualControlRequired
            createdAt
            createdBy
            updatedAt
            updatedBy
        }
    }
`;

const RECORD_APPROVAL_DECISION = gql`
    mutation RecordApprovalDecision($input: RecordApprovalDecisionInput!) {
        recordApprovalDecision(input: $input) {
            instanceId
            templateId
            templateVersion
            tenant
            domain
            criticality
            requiredApprovers {
                role
                certifications
                minimumExperience
            }
            status
            decisions {
                decisionId
                approverId
                approverName
                approverRole
                decisionType
                comments
                revisionItems {
                    fieldId
                    issue
                    suggestedFix
                }
                decidedAt
            }
            deadline
            dualControlRequired
            createdAt
            createdBy
            updatedAt
            updatedBy
        }
    }
`;

// ============================================================================
// Repository Implementation
// ============================================================================

/**
 * Approval Repository
 *
 * Provides domain-agnostic access to approval workflow instances
 */
export class ApprovalRepository extends BaseRepository<ApprovalWorkflowInstance> {
    /**
     * Get approval workflow instance by ID
     *
     * @param instanceId - Approval instance ID
     * @returns Approval workflow instance or null if not found
     */
    async getInstance(instanceId: string): Promise<ApprovalWorkflowInstance | null> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: GET_APPROVAL_INSTANCE,
                variables: { instanceId },
            });

            return result.data?.getApprovalWorkflowInstance || null;
        } catch (error) {
            console.error('Failed to get approval instance:', error);
            throw new Error(`Failed to get approval instance: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * List pending approvals for specific approver
     *
     * Domain-agnostic: Works across all approval workflows
     *
     * @param tenant - Tenant ID
     * @param approverId - Approver user ID
     * @returns List of pending approval instances
     */
    async listPendingApprovals(tenant: string, approverId: string): Promise<ApprovalWorkflowInstance[]> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: LIST_PENDING_APPROVALS,
                variables: { tenant, approverId },
            });

            return result.data?.listPendingApprovals || [];
        } catch (error) {
            console.error('Failed to list pending approvals:', error);
            throw new Error(`Failed to list pending approvals: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Record approval decision
     *
     * Life-critical: Complete audit trail with approver identity
     *
     * @param input - Decision input (approverId, decision type, comments, revisions)
     * @returns Updated approval workflow instance
     */
    async recordDecision(input: RecordDecisionInput): Promise<ApprovalWorkflowInstance> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: RECORD_APPROVAL_DECISION,
                variables: { input },
            });

            if (!result.data?.recordApprovalDecision) {
                throw new Error('Failed to record approval decision');
            }

            // Invalidate cache for this instance
            this.cache.invalidate(input.instanceId);

            return result.data?.recordApprovalDecision ?? null;
        } catch (error) {
            console.error('Failed to record approval decision:', error);
            throw new Error(`Failed to record approval decision: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get approval history for template
     *
     * @param templateId - Template ID
     * @returns List of all approval instances for this template
     */
    async getApprovalHistory(templateId: string): Promise<ApprovalWorkflowInstance[]> {
        try {
            // TODO: Implement when backend resolver is added
            // const result = await this.graphQLClient.query<Record<string, any>>({
            //     query: GET_APPROVAL_HISTORY,
            //     variables: { templateId },
            // });

            // For now, return empty array
            console.warn('getApprovalHistory not yet implemented in backend');
            return [];
        } catch (error) {
            console.error('Failed to get approval history:', error);
            throw new Error(`Failed to get approval history: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Submit template for approval
     *
     * @param templateId - Template ID to submit
     * @param templateVersion - Template version
     * @param submitterId - User ID of submitter
     * @returns Created approval workflow instance
     */
    async submitForApproval(
        templateId: string,
        templateVersion: string,
        submitterId: string
    ): Promise<ApprovalWorkflowInstance> {
        try {
            // TODO: Implement when backend resolver is added
            // const result = await this.graphQLClient.mutate<Record<string, any>>({
            //     mutation: SUBMIT_TEMPLATE_FOR_APPROVAL,
            //     variables: { templateId, templateVersion, submitterId },
            // });

            throw new Error('submitForApproval not yet implemented in backend');
        } catch (error) {
            console.error('Failed to submit template for approval:', error);
            throw new Error(`Failed to submit template for approval: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}

// Export singleton instance
export const approvalRepository = new ApprovalRepository();
